import {
  JourneyReviewTabs,
  ReviewType,
} from 'modules/workflows/components/JourneyReview/JourneyReview.constants';
import {
  getIsWorkflowSaving,
  getCombos,
  getWorkflowHasUnsavedChanges,
  updateNodeComboId,
} from 'modules/workflows/ducks';
import { useGetJourneyOptimizations } from 'modules/workflows/hooks';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Optimization } from 'modules/workflows/types';
import { CompletedItem, PerformanceItem } from './JourneyReviewContext.types';
import {
  getPerformanceIds,
  getTilesFromComboId,
  hydratePerformanceErrors,
  parseFetchedOptimizations,
} from './JourneyReviewContext.utils';

export const useJourneyPerformanceOptimizations = ({
  activeTab,
  onOptimizationComplete,
}: {
  activeTab: JourneyReviewTabs;
  onOptimizationComplete: (tileIds: string[]) => void;
}) => {
  const [completed, setCompleted] = useState<CompletedItem[]>([]);
  const [performanceData, setPerformanceTabData] = useState<PerformanceItem[]>([]);
  const [shouldHighlightOptimizations, setShouldHighlightOptimizations] = useState(false);
  const isJourneySaving = useSelector(getIsWorkflowSaving);
  const comboIds = useSelector(getCombos);
  const dispatch = useDispatch();
  const hasUnsavedChanges = useSelector(getWorkflowHasUnsavedChanges);

  const performanceIds = useMemo(() => getPerformanceIds(performanceData), [performanceData]);

  const highlightOptimizations = () => {
    Object.entries(performanceIds).forEach(([comboId, tileIds]) => {
      dispatch(updateNodeComboId(tileIds, comboId));
    });
    setShouldHighlightOptimizations(false);
  };

  useEffect(() => {
    if (!hasUnsavedChanges && shouldHighlightOptimizations) {
      highlightOptimizations();
    }
  }, [hasUnsavedChanges, shouldHighlightOptimizations]);

  const onGetOptimizationsSuccess = (data: Optimization[]) => {
    setCompleted([]);
    const tabData = parseFetchedOptimizations(data);

    setPerformanceTabData(tabData);

    if (activeTab === JourneyReviewTabs.Performance) {
      const nextPerformanceIds = getPerformanceIds(tabData);
      Object.entries(nextPerformanceIds).forEach(([comboId, tileIds]) => {
        dispatch(updateNodeComboId(tileIds, comboId));
      });
    }
  };

  const {
    getOptimizations,
    isLoading: isOptimizationsLoading,
    hasError: hasOptimizationError,
  } = useGetJourneyOptimizations({
    onGetOptimizationsSuccess,
  });

  const optimizations = useMemo(() => {
    const hydratedPerformanceItems = hydratePerformanceErrors(performanceData, completed);

    return hydratedPerformanceItems.sort((a, b) => a.tileId.localeCompare(b.tileId));
  }, [performanceData, completed]);

  const handleSetPerformanceTabData = () =>
    setPerformanceTabData(performanceData.filter((item) => item.showHighlight));

  const completeOptimizations = (optimizationIds: string[]) => {
    const tileIdsFromCombos = optimizationIds.reduce((acc: string[], comboId) => {
      const ids = getTilesFromComboId(comboId);
      return [...acc, ...ids];
    }, []);
    const tileIds = tileIdsFromCombos || [];
    const tabData = performanceData.map((item) => {
      return {
        ...item,
        showHighlight: item.showHighlight ? !optimizationIds.includes(item.comboId) : false,
      };
    });

    // Update tiles in issues tab
    onOptimizationComplete(tileIds);
    // Remove existing combo IDs
    dispatch(updateNodeComboId(tileIds));
    // Re-fetch and apply new combo IDs
    setShouldHighlightOptimizations(true);
    // Set updated performance tab data
    setPerformanceTabData(tabData);
    // Updated completed optimizations list
    optimizationIds.forEach((id) => {
      setCompleted((completedItems) => [
        ...completedItems,
        { id, reviewType: ReviewType.Performance },
      ]);
    });
  };

  const hideOptimizations = () => {
    handleSetPerformanceTabData();
    dispatch(updateNodeComboId(Object.values(performanceIds).flatMap((tileIds) => tileIds)));
  };

  const incompleteOptimizationsCount =
    comboIds.length || performanceData.filter((item) => item.showHighlight).length;

  return {
    optimizations,
    getOptimizations,
    isOptimizationsLoading: !!isOptimizationsLoading || !!isJourneySaving,
    hasOptimizationError,
    highlightOptimizations,
    hideOptimizations,
    completeOptimizations,
    clearCompletedOptimizations: () => setCompleted([]),
    incompleteOptimizationsCount,
  };
};
